<template>
  <div>
    <transition name="modal">
      <delete-modal
        v-if="showDeleteModal"
        :loading="loadingDelete"
        @close="closeDeleteModal()"
        @confirmDelete="deleteElement"
      ></delete-modal>
    </transition>

    <transition name="modal">
      <pet-history-modal
        v-if="previousStateToView"
        :previousState="previousStateToView"
        @close="previousStateToView = null"
      ></pet-history-modal>
    </transition>

    <transition name="modal">
      <diet-form
        v-if="showDietForm"
        :petId="pet.id"
        :dietCategories="dietCategories"
        :dietStages="dietStages"
        :dietTypes="dietTypes"
        @dietCreated="dietCreated"
        @close="showDietForm = false"
      ></diet-form>
    </transition>

    <transition name="modal">
      <pet-form-modal
        v-if="showPetForm"
        :petId="pet.id"
        @close="showPetForm = false"
        @petUpdated="loadPet()"
      ></pet-form-modal>
    </transition>

    <transition name="modal">
      <confirmation-modal
        v-if="showConfirmationModal"
        :message="'¿Estás seguro que deseas solicitar al cliente una reevaluación de la mascota?'"
        @close="showConfirmationModal = false"
        @confirm="reevaluate()"
      ></confirmation-modal>
    </transition>

    <div v-if="!loading">
      <div v-if="pet" class="flex flex-col">
        <div class="card">
          <card-header :title="'Mascota #' + pet.id">
            <template #actions>
              <router-link
                v-if="hasPermission('edit_pets')"
                :to="{
                  name: 'petsEditForm',
                  params: { id: pet.id },
                }"
              >
                <edit-button />
              </router-link>

              <delete-button
                v-if="hasPermission('delete_pets')"
                @click.prevent="openDeleteModal()"
              />
            </template>
          </card-header>

          <div class="w-20 h-20 sm:w-40 sm:h-40 relative flex mx-auto">
            <img
              ref="imagePreview"
              class="rounded-full object-cover object-center w-full h-full border border-primary-500"
              :class="[{ 'animate-pulse': savingImage }]"
              :src="imagePreview"
            />

            <input
              class="hidden"
              type="file"
              name="image"
              :id="'profileImage' + pet.id"
              ref="image"
              accept="image/*"
              @change="getImage"
            />

            <div v-if="!savingImage">
              <label
                v-if="!newImage"
                :for="'profileImage' + pet.id"
                class="cursor-pointer smooth hover:bg-primary-600 w-10 h-10 flex items-center bg-primary-500 rounded-full border-4 border-white absolute -right-2 -bottom-2 sm:right-0 sm:bottom-0"
              >
                <camera-icon class="w-4 stroke-current mx-auto text-white" />
              </label>
            </div>
          </div>

          <div v-if="newImage" class="flex space-x-2 mt-2 mx-auto">
            <button
              class="delete-button"
              @click.prevent="
                getImagePreview();
                newImage = null;
              "
            >
              Remover
            </button>

            <button
              class="secondary-button"
              @click.prevent="saveProfilePicture()"
            >
              Guardar
            </button>
          </div>

          <p class="mt-4 font-medium text-lg text-center">
            {{ pet.name }}
          </p>

          <div class="div-line"></div>

          <section-header title="Informacion General" />

          <div class="detail-grid">
            <detail-field label="Raza" :value="pet.custom_breed || pet.breed.name" />
            <detail-field label="Tipo" :value="pet.breed.pet_type.name" />
            <detail-field label="Género" :value="pet.gender" />
            <detail-field label="Edad" :value="getPetAge(pet.birthdate) + ' - ' + formatDate(pet.birthdate)" />
          </div>
        </div>

        <section-header title="Dietas" />

        <button
          v-if="hasPermission('edit_diets')"
          class="primary-button mx-0 mt-2"
          @click.prevent="showDietForm = true"
        >
          Crear Dieta
        </button>

        <div v-if="pet.diets.length === 0">
          <p class="text-sm font-medium">
            No existe una dieta para esta mascota.
          </p>
        </div>

        <diet
          v-for="diet in pet.diets"
          :key="diet.id"
          class="mt-6"
          :diet="diet"
          :pet="pet"
          :dietCategories="dietCategories"
          :dietStages="dietStages"
          :dietTypes="dietTypes"
          :canEdit="true"
          :canDelete="true"
          @dietUpdated="loadPet()"
        />

        <section-header title="Evaluaciones" />

        <div v-if="pet.status !== 'reevaluation_needed'">
          <p class="font-medium text-sm mt-2">
            ¿El cliente requiere reevaluar a su mascota?
          </p>
          <button
            class="secondary-button"
            @click.prevent="showConfirmationModal = true"
          >
            Solicitar al cliente una nueva evaluación
          </button>
        </div>

        <div v-else>
          <p class="text-error-500">Esta mascota requiere una reevaluación.</p>
        </div>

        <router-link
          v-if="hasPermission('edit_pets')"
          :to="{
            name: 'petCheckupsCreateForm',
            params: { petId: pet.id, selectedType: pet.breed.pet_type.id },
          }"
          class="primary-button mt-2 w-fit mx-0"
        >
          Agregar Evaluación
        </router-link>

        <div v-for="checkup in checkups" :key="checkup.id">
          <evaluation
            class="mt-4"
            :checkup="checkup"
            :pet="pet"
            @createdImage="loadCheckups()"
            @deletedImage="loadCheckups()"
            @createdFile="loadCheckups()"
            @deletedFile="loadCheckups()"
            @deletedCheckup="
              currentPage = 1;
              loadCheckups();
            "
          />
        </div>

        <pagination
          :current-page="currentPage"
          :total-pages="totalPages"
          @page-change="onPageChange"
        />

        <section-header title="Comentarios" />

        <div
          v-for="comment in pet.comments"
          :key="comment.id"
          class="card w-full my-2"
        >
          <p class="text-sm font-medium">
            {{ comment.commenter }}
          </p>

          <p class="text-sm">
            {{ comment.comment }}
          </p>

          <p class="text-xs text-content-400">
            {{ formatDatetime(comment.updated_at) }}
          </p>
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import deleteMixin from "@/mixins/deleteMixin";
import DietForm from "@/components/forms/DietForm";
import Diet from "@/components/models/Diet";
import Evaluation from "@/components/models/Evaluation";
import PetHistoryModal from "@/components/modals/PetHistoryModal";
import PetFormModal from "@/components/modals/PetFormModal";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import CameraIcon from "@/icons/CameraIcon";
import Pagination from "@/components/ui/Pagination.vue";
import { formatDate, formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Pet Detail",

  components: {
    DietForm,
    Diet,
    Evaluation,
    PetHistoryModal,
    PetFormModal,
    ConfirmationModal,
    CameraIcon,
    Pagination,
  },

  data() {
    return {
      dietCategories: null,
      dietStages: null,
      dietTypes: null,
      pet: null,
      loading: false,
      loadingDelete: false,
      showDietForm: false,
      previousStateToView: null,
      savingImage: false,
      imagePreview: null,
      newImage: null,
      checkups: null,
      currentPage: 1,
      totalPages: 0,
      defaultImage: require("@/assets/paw.png"),
      showPetForm: false,
      showConfirmationModal: false,
    };
  },

  mixins: [deleteMixin],

  created() {
    this.$store.commit("setSelectedTab", "pets");

    this.loadDietCategories();
    this.loadDietStages();
    this.loadDietTypes();
    this.loadPet();
  },

  methods: {
    formatDate,

    formatDatetime,

    integerToCurrency,

    async loadDietCategories() {
      this.loading = true;

      try {
        this.dietCategories = await api.resources.getDietCategories();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: 'petsIndex' });
      }

      this.loading = false;
    },

    async loadDietStages() {
      this.loading = true;

      try {
        this.dietStages = await api.resources.getDietStages();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: 'petsIndex' });
      }

      this.loading = false;
    },

    async loadDietTypes() {
      this.loading = true;

      try {
        this.dietTypes = await api.resources.getDietTypes();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: 'petsIndex' });
      }

      this.loading = false;
    },

    async loadPet() {
      this.loading = true;

      try {
        this.pet = await api.pets.getById(this.$route.params.id);
        this.$store.commit('setBreadcrumbLabel', {
          routeName: this.$route.name,
          label: this.pet.name,
        });
        this.pet.diets.forEach((diet) => {
          diet.collapsed = true;
        });
        this.loadCheckups();
        this.getImagePreview();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: 'petsIndex' });
      }

      this.loading = false;
    },

    async loadCheckups() {
      this.loading = true;

      try {
        const response = await api.pets.getCheckups(this.$route.params.id, [
          {
            name: "page",
            value: this.currentPage,
          },
        ]);
        this.checkups = response.data;
        this.currentPage = response.current_page;
        this.totalPages = response.last_page;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    getImagePreview() {
      if (this.pet.profile_image) {
        this.imagePreview = this.pet.profile_image.url;
      } else {
        this.imagePreview = require("@/assets/paw.png");
      }
    },

    getPetAge(birthdate) {
      const birth = new Date(birthdate);
      const now = new Date();

      let years = now.getFullYear() - birth.getFullYear();
      let months = now.getMonth() - birth.getMonth();
      let days = now.getDate() - birth.getDate();

      if (days < 0) {
        months -= 1;
      }

      if (months < 0) {
        years -= 1;
        months += 12;
      }

      if (years < 1) {
        return `${months} ${months === 1 ? "mes" : "meses"}`;
      }

      return `${years} ${years === 1 ? "año" : "años"}`;
    },

    selectHistory(history) {
      const previousState = JSON.parse(history.previous_state);
      this.previousStateToView = previousState;
    },

    getOptions() {
      let options = [];

      if (this.hasPermission("delete_pets")) {
        options.push({
          action: "delete",
          label: "Eliminar",
        });
      }

      return options;
    },

    prepareDietFormat(diet) {
      let dietFormatted = JSON.parse(JSON.stringify(diet));

      dietFormatted.meals.forEach((meal) => {
        meal.portions.forEach((portion) => {
          portion.diet_category_id = portion.diet_subcategory.diet_category_id;
          portion.saved = true;

          delete portion.id;
          delete portion.meal_id;
          delete portion.meal_id;
          delete portion.diet_subcategory;
          delete portion.created_at;
          delete portion.updated_at;
        });

        delete meal.id;
        delete meal.diet_id;
        delete meal.created_at;
        delete meal.updated_at;
      });

      delete dietFormatted.collapsed;
      delete dietFormatted.created_at;
      delete dietFormatted.updated_at;
      delete dietFormatted.diet_stage;
      delete dietFormatted.diet_type;
      delete dietFormatted.quote_id;

      return dietFormatted;
    },

    dietCreated() {
      this.showDietForm = false;
      this.loadPet();
    },

    getImage(e) {
      let image = e.target.files[0];
      if (image) {
        let url = URL.createObjectURL(image);
        let img = new Image();

        img.onload = () => {
          URL.revokeObjectURL(url);
          let reader = new FileReader();
          reader.onload = (e) => {
            this.newImage = image;
            this.imagePreview = e.target.result;
          };
          reader.readAsDataURL(image);
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          alert("Unable to load image");
        };

        img.src = url;
      }
    },

    async deleteElement() {
      this.loadingDelete = true;

      if (this.deleteDiet) {
        try {
          const response = await api.diets.delete(this.pet.diets[0].id);
          this.$store.dispatch("successSnack", response.message);
          this.closeDeleteModal();
          this.loadPet();
          this.deleteDiet = false;
        } catch (error) {
          this.$store.dispatch("errorSnack", error?.response?.data?.message);
        }
      } else {
        try {
          const response = await api.pets.delete(this.pet.id);
          this.$store.dispatch("successSnack", response.message);
          this.closeDeleteModal();
          this.$router.push({ name: 'petsIndex' });
        } catch (error) {
          this.$store.dispatch("errorSnack", error?.response?.data?.message);
        }
      }

      this.loadingDelete = false;
    },

    async saveProfilePicture() {
      if (!this.newImage) {
        return;
      }

      const formData = new FormData();

      formData.append("pet_id", this.pet.id);
      formData.append("image", this.newImage);

      this.savingImage = true;

      try {
        const response = await api.pets.saveProfilePicture(formData);
        this.$store.dispatch("successSnack", response.message);
        this.loadPet();
        this.newImage = null;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.savingImage = false;
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadCheckups();
    },

    async reevaluate() {
      this.showConfirmationModal = false;
      this.loading = true;

      try {
        const response = await api.pets.reevaluate(this.pet.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadPet();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },
  },

  watch: {
    "$route.params": {
      immediate: true,
      handler() {
        this.loadPet();
      },
    },
  },
};
</script>
